import { isTokenRefreshError } from "@/utils/backendError";

type SessionExpiredListener = () => void;

const listeners = new Set<SessionExpiredListener>();

// Latched so a page with ten queries in flight produces one logout, not ten.
// Re-armed by armSessionExpiry() when a fresh token is installed.
let reported = false;

/**
 * Report a failed API call so the app can notice an expired session.
 *
 * Every path that issues a request through the JWT client calls this on
 * rejection: the token refresh happens inside the client's own interceptor,
 * so the 401 surfaces as a rejection of whatever request triggered it and
 * there is no other point at which "the session is gone" is knowable.
 * Non-refresh failures are ignored, so callers can hand over every error.
 */
export function reportSessionExpiry(error: unknown): void {
  if (reported || !isTokenRefreshError(error)) return;
  // Nothing is listening yet (a failure during boot, before the router
  // mounts). Stay un-latched so the next failed request still reports.
  if (listeners.size === 0) return;

  reported = true;
  listeners.forEach((listener) => listener());
}

/**
 * Re-arm reporting for a newly established session. Without this the first
 * expiry would be the only one the app ever reacts to per page load.
 */
export function armSessionExpiry(): void {
  reported = false;
}

export function onSessionExpired(listener: SessionExpiredListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
